import { useEffect, useMemo, useRef } from "react";
import { getCookie } from "../../utils/CookieHelper";
import { COOKIE_ENUM } from "../../utils/enums";
import envConfig from "../../utils/env.config";
import { parseJwt } from "../../utils/help/user/user";
import { tokenPayload } from "../../utils/types";
import { fetchLoyaltyCustomerInfo } from "../redux/http/getLoyaltyCustomerInfoThunk";
import { fetchLoyaltyTierSystem } from "../redux/http/getLoyaltyTierSystemThunk";
import {
  selectIsVerifiedOrganizationCustomer,
  selectUseLoyaltyService,
} from "../redux/selectors/loyaltyServiceSelectors";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";

/**
 * Refetches loyalty proxy data app-wide (mounted from BackgroundHooks).
 * Customer info refetches when token or receipt-related fields change.
 * Tier system uses GET tier-system/customer (JWT); loyalty proxy treats visitors
 * and returns catalog / first tier as configured server-side.
 */
/** Nothing polls the loyalty proxy more often than this. */
const REFETCH_MIN_GAP_MS = 30_000;

type CustomerInfoTarget = {
  customerShortId?: string;
};

/**
 * Who the customer-info call is for, or null when nobody can be identified and
 * the call must not be made.
 *
 * The token decides which branch applies, not the restaurant. A visitor token
 * is an offline-mode guest, identified by the customer short id carried in from
 * the QR link, which the proxy resolves server-side. Any other token identifies
 * itself, and a short id is not sent even when one is lying around - the proxy
 * only accepts a signed-in caller's own, so a stale id from a forwarded
 * /r/<shortId> link would just be refused.
 *
 * A non-visitor token with no phone number (mid-signup, or the partnerlogin
 * organization flow) cannot be answered for either way, and must bail rather
 * than fall through to the guest branch: the proxy would reject it, and the
 * interval refetch below does not suppress rejected requests, so it would retry
 * every 60s for as long as the tab is open.
 */
/**
 * The tier-system call takes the same guest short id. Visitor tokens do name a
 * customer (createVisitor makes a shell row), but it is not the guest's, so
 * without the short id loyalty answers in catalog mode.
 */
const resolveTierSystemShortId = (
  decodedToken: tokenPayload | undefined,
  offlineShortId: string | undefined
): string | undefined =>
  decodedToken?.visitor && offlineShortId ? offlineShortId : undefined;

const resolveCustomerInfoTarget = (
  decodedToken: tokenPayload | undefined,
  offlineShortId: string | undefined
): CustomerInfoTarget | null => {
  if (decodedToken?.visitor) {
    return offlineShortId ? { customerShortId: offlineShortId } : null;
  }

  return decodedToken?.customer_id && decodedToken?.phone_number ? {} : null;
};

export const useLoyaltyDataRefetch = () => {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.userInfo.token);
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const payScreenReceipt = useAppSelector(
    (state) => state.receipt.payScreenReceipt
  );
  const currentReceiptId = useAppSelector(
    (state) => state.receipt.currentReceiptId
  );
  const previousOrders = useAppSelector(
    (state) => state.receipt.previousOrders
  );
  const organizationCustomerVerified = useAppSelector(
    selectIsVerifiedOrganizationCustomer
  );
  // The loyalty partnership flag only changes after this refetch, so verification itself triggers it.
  const partnerEmailVerified = useAppSelector(
    (state) => !!state.partnerAccount.organizationCustomer?.verified
  );
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );
  const customerShortId = useAppSelector(
    (state) => state.userInfo.customerShortId
  );
  // Both shapes are populated depending on load order (see
  // selectUseLoyaltyService), so consult each before deciding a restaurant is
  // not an offline-mode one.
  const offlineShortId =
    restaurantConfig?.hasOfflineMode ?? restaurantInfo?.config?.hasOfflineMode
      ? customerShortId
      : undefined;
  // Mirrored in refs so the polling effect can consult them without
  // re-subscribing every time a request settles.
  const tierSystemStatus = useAppSelector(
    (state) => state.loyaltyTierSystem?.status
  );
  const customerInfoStatus = useAppSelector(
    (state) => state.loyaltyCustomerInfo?.status
  );
  const statusRef = useRef({ tierSystemStatus, customerInfoStatus });
  const lastRefetchRef = useRef(0);

  useEffect(() => {
    statusRef.current = { tierSystemStatus, customerInfoStatus };
  }, [customerInfoStatus, tierSystemStatus]);

  const previousOrdersSignature = useMemo(
    () => previousOrders.map((r) => `${r.id}:${r.state}`).join("|"),
    [previousOrders]
  );

  const resolvedRestaurantId = useMemo(() => {
    const expectedRestaurantId = selectedFranchise?.restaurantId;
    const currentRestaurantId = restaurantInfo?.id;
    const id =
      expectedRestaurantId ||
      currentRestaurantId ||
      parseInt(envConfig.restaurantId!, 10);

    return Number.isFinite(id) && id > 0 ? id : null;
  }, [restaurantInfo?.id, selectedFranchise?.restaurantId]);

  useEffect(() => {
    if (!useLoyaltyService) {
      return;
    }

    const cookieToken = getCookie(COOKIE_ENUM.TOKEN);
    if (!cookieToken) {
      return;
    }

    const decodedToken = parseJwt(cookieToken as string);
    const target = resolveCustomerInfoTarget(decodedToken, offlineShortId);

    if (!target) {
      return;
    }

    const expectedRestaurantId = selectedFranchise?.restaurantId;
    const currentRestaurantId = restaurantInfo?.id;
    const restaurantId =
      expectedRestaurantId ||
      currentRestaurantId ||
      parseInt(envConfig.restaurantId!, 10);

    dispatch(
      fetchLoyaltyCustomerInfo({
        restaurantId,
        includes: "tierProgress",
        ...target,
      })
    );
  }, [
    dispatch,
    useLoyaltyService,
    token,
    offlineShortId,
    payScreenReceipt?.id,
    payScreenReceipt?.state,
    currentReceiptId,
    previousOrdersSignature,
    restaurantInfo?.id,
    selectedFranchise?.restaurantId,
    organizationCustomerVerified,
    partnerEmailVerified,
  ]);

  useEffect(() => {
    if (!useLoyaltyService) {
      return;
    }

    const cookieToken = getCookie(COOKIE_ENUM.TOKEN);
    if (!cookieToken) {
      return;
    }

    if (resolvedRestaurantId == null) {
      return;
    }

    const customerShortIdParam = resolveTierSystemShortId(
      parseJwt(cookieToken as string),
      offlineShortId
    );

    dispatch(
      fetchLoyaltyTierSystem({
        restaurantId: resolvedRestaurantId,
        ...(customerShortIdParam && { customerShortId: customerShortIdParam }),
      })
    );
  }, [
    dispatch,
    useLoyaltyService,
    token,
    resolvedRestaurantId,
    offlineShortId,
  ]);

  // Open tabs otherwise never converge: offers/tiers changed server-side
  // (e.g. an offer deactivated in retool) stay stale until a token or receipt
  // change. Refetch when the tab regains visibility and on a slow interval
  // while visible. Loyalty-flag gated, so legacy restaurants add no traffic.
  useEffect(() => {
    if (!useLoyaltyService || resolvedRestaurantId == null) {
      return;
    }

    const refetch = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      // Switching tabs back and forth fires visibilitychange each time, and the
      // interval keeps ticking underneath it. Neither is worth a fresh pair of
      // requests when the last ones just landed.
      const now = Date.now();
      if (now - lastRefetchRef.current < REFETCH_MIN_GAP_MS) {
        return;
      }
      lastRefetchRef.current = now;

      const cookieToken = getCookie(COOKIE_ENUM.TOKEN);
      const decodedToken = cookieToken
        ? parseJwt(cookieToken as string)
        : undefined;

      if (statusRef.current.tierSystemStatus !== "loading") {
        const customerShortIdParam = resolveTierSystemShortId(
          decodedToken,
          offlineShortId
        );

        dispatch(
          fetchLoyaltyTierSystem({
            restaurantId: resolvedRestaurantId,
            ...(customerShortIdParam && {
              customerShortId: customerShortIdParam,
            }),
          })
        );
      }

      const target = resolveCustomerInfoTarget(decodedToken, offlineShortId);
      if (target && statusRef.current.customerInfoStatus !== "loading") {
        dispatch(
          fetchLoyaltyCustomerInfo({
            restaurantId: resolvedRestaurantId,
            includes: "tierProgress",
            ...target,
          })
        );
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    const intervalId = setInterval(refetch, 60_000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(intervalId);
    };
  }, [dispatch, useLoyaltyService, resolvedRestaurantId, offlineShortId]);
};
